import React, { useState, useEffect, useRef } from 'react';
import {
  ShieldCheck,
  Smartphone,
  Mail,
  ArrowRight,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  ArrowLeft,
  Lock,
  Check,
} from 'lucide-react';
import {
  sendTwoFactorCodeApi,
  verifyTwoFactorCodeApi,
} from '../../lib/supabaseService';

interface PhoneOtpVerificationStepProps {
  phone?: string;
  email?: string;
  fullName?: string;
  userType: 'customer' | 'professional';
  purpose?: 'registration' | 'login_2fa';
  challengeToken?: string;
  initialRegistrationSessionId?: string;
  initialActiveStage?: 'email' | 'phone';
  onVerified: (verifyResult?: any) => void;
  onBack: () => void;
}

export const PhoneOtpVerificationStep: React.FC<PhoneOtpVerificationStepProps> = ({
  phone = '',
  email = '',
  fullName = '',
  userType,
  purpose = 'registration',
  challengeToken,
  initialRegistrationSessionId,
  initialActiveStage = 'email',
  onVerified,
  onBack,
}) => {
  const [digits, setDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [cooldown, setCooldown] = useState(60);
  const [remainingAttempts, setRemainingAttempts] = useState<number | null>(null);

  // Sequential registration state (Stage 3: 'email' -> Stage 4: 'phone')
  const [registrationSessionId, setRegistrationSessionId] = useState<string>(
    initialRegistrationSessionId || ''
  );
  const [seqStage, setSeqStage] = useState<'email' | 'phone'>(initialActiveStage);
  const [emailStageCompleted, setEmailStageCompleted] = useState<boolean>(
    initialActiveStage === 'phone'
  );
  const [phoneStageCompleted, setPhoneStageCompleted] = useState<boolean>(false);
  const [stageFailed, setStageFailed] = useState<boolean>(false);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Mask phone for display: +234 80******78
  const formatMaskedPhone = (raw: string) => {
    const digitsOnly = raw.replace(/\D/g, '');
    if (digitsOnly.length >= 10) {
      const prefix = digitsOnly.startsWith('234') ? '+234' : '0';
      const localDigits = digitsOnly.startsWith('234') ? digitsOnly.slice(3) : digitsOnly.slice(1);
      if (localDigits.length >= 9) {
        return `${prefix} ${localDigits.slice(0, 2)}******${localDigits.slice(-2)}`;
      }
    }
    return raw;
  };

  // Mask email for display: j***@example.com
  const formatMaskedEmail = (raw?: string) => {
    if (!raw || !raw.includes('@')) return '';
    const [user, domain] = raw.split('@');
    if (user.length <= 2) {
      return `${user.charAt(0)}***@${domain}`;
    }
    return `${user.slice(0, 2)}***${user.slice(-1)}@${domain}`;
  };

  // Cooldown countdown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const interval = setInterval(() => {
      setCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [cooldown]);

  // Focus first digit on mount or stage switch
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [seqStage]);

  const handleDigitChange = (index: number, val: string) => {
    setError('');
    setStageFailed(false);
    const clean = val.replace(/\D/g, '');

    if (clean.length <= 1) {
      const newDigits = [...digits];
      newDigits[index] = clean;
      setDigits(newDigits);

      if (clean && index < 5 && inputRefs.current[index + 1]) {
        inputRefs.current[index + 1]?.focus();
      }
      return;
    }

    if (clean.length >= 6) {
      const pasted = clean.slice(0, 6).split('');
      setDigits(pasted);
      if (inputRefs.current[5]) {
        inputRefs.current[5]?.focus();
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      const newDigits = [...digits];
      newDigits[index - 1] = '';
      setDigits(newDigits);
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '');
    if (pastedData.length >= 6) {
      const pasted = pastedData.slice(0, 6).split('');
      setDigits(pasted);
      if (inputRefs.current[5]) {
        inputRefs.current[5]?.focus();
      }
    }
  };

  // Trigger Stage 4 (Phone SMS OTP) automatically right after Stage 3 (Email OTP) succeeds
  const dispatchStage4PhoneOtp = async (sessionIdToUse: string) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/auth/registration/phone-otp/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: sessionIdToUse,
          email: email.trim().toLowerCase(),
          phone: phone.trim(),
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSeqStage('phone');
        setDigits(['', '', '', '', '', '']);
        setRemainingAttempts(null);
        setCooldown(data.cooldownSeconds || 60);
        setSuccessMessage(
          `Step 3 (Email) verified! A separate 6-digit SMS code has now been sent to ${formatMaskedPhone(phone)}.`
        );
      } else {
        setStageFailed(true);
        setError(data.message || data.error || 'Failed to send SMS verification code to your phone.');
      }
    } catch (err: any) {
      setStageFailed(true);
      setError(err?.message || 'Network error while sending SMS verification code.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setError('');
    setStageFailed(false);
    const otpCode = digits.join('');

    if (otpCode.length !== 6) {
      setError('Please enter the complete 6-digit verification code.');
      return;
    }

    setLoading(true);
    try {
      // 1. Login 2FA Flow
      if (purpose === 'login_2fa') {
        const res = await verifyTwoFactorCodeApi({
          phone: phone.trim() || undefined,
          email: email?.trim(),
          otp: otpCode,
          challengeToken,
          purpose: 'login_2fa',
        });
        if (res.success) {
          setSuccessMessage('Two-factor authentication verified. Completing sign-in...');
          setTimeout(() => {
            onVerified(res);
          }, 400);
        } else {
          setStageFailed(true);
          setError(res.message || 'Invalid verification code. Please check and try again.');
          if (typeof res.remainingAttempts === 'number') {
            setRemainingAttempts(res.remainingAttempts);
          }
        }
        return;
      }

      // 2. Unified Registration OTP Flow (Same 6-digit OTP sent to both Email & SMS)
      const verifyEndpoint =
        seqStage === 'phone'
          ? '/api/auth/registration/phone-otp/verify'
          : '/api/auth/registration/email-otp/verify';

      const res = await fetch(verifyEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: registrationSessionId,
          email: email.trim().toLowerCase(),
          phone: phone.trim(),
          otp: otpCode,
        }),
      });
      const data = await res.json();

      if (res.ok && data.success) {
        setEmailStageCompleted(true);
        setPhoneStageCompleted(true);
        const nextSessionId = data.sessionId || registrationSessionId;
        setRegistrationSessionId(nextSessionId);

        if (data.verificationProofToken) {
          setSuccessMessage(
            'Verification code confirmed! Activating your account...'
          );
          setTimeout(() => {
            onVerified({
              ...data,
              registrationSessionId: nextSessionId,
              verificationProofToken: data.verificationProofToken,
              emailVerified: true,
              phoneVerified: true,
            });
          }, 400);
          return;
        }

        // Fallback if verificationProofToken wasn't returned on first call
        await dispatchStage4PhoneOtp(nextSessionId);
      } else {
        setStageFailed(true);
        setError(data.message || data.error || 'Incorrect verification code. Please check your Email or SMS and try again.');
        if (typeof data.remainingAttempts === 'number') {
          setRemainingAttempts(data.remainingAttempts);
        }
      }
    } catch (err: any) {
      setStageFailed(true);
      setError(err?.message || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0 || resending) return;
    setError('');
    setStageFailed(false);
    setSuccessMessage('');
    setResending(true);

    try {
      if (purpose === 'login_2fa') {
        const res = await sendTwoFactorCodeApi({
          phone: phone.trim() || undefined,
          email: email?.trim(),
          challengeToken,
          fullName,
          userType,
          purpose: 'login_2fa',
        });
        if (res.success) {
          setCooldown(res.cooldownSeconds || 60);
          setDigits(['', '', '', '', '', '']);
          setSuccessMessage('A new 6-digit 2FA security code has been sent.');
          inputRefs.current[0]?.focus();
        } else {
          setError(res.message || 'Failed to resend code.');
          if (res.cooldownSeconds) setCooldown(res.cooldownSeconds);
        }
        return;
      }

      const res = await fetch('/api/auth/registration/otp/resend', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: registrationSessionId || undefined,
          stage: 'email',
          role: userType,
          fullName: fullName.trim(),
          email: email.trim().toLowerCase(),
          phone: phone.trim(),
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        if (data.sessionId) setRegistrationSessionId(data.sessionId);
        setCooldown(data.cooldownSeconds || 60);
        setDigits(['', '', '', '', '', '']);
        setRemainingAttempts(null);
        setSuccessMessage(
          `A new 6-digit verification code has been sent to both ${formatMaskedEmail(email)} and ${formatMaskedPhone(phone)}.`
        );
        inputRefs.current[0]?.focus();
      } else {
        setError(data.message || data.error || 'Failed to resend verification code.');
        if (data.cooldownSeconds) setCooldown(data.cooldownSeconds);
        if (data.retryAfterSeconds) setCooldown(Number(data.retryAfterSeconds));
      }
    } catch (err: any) {
      setError(err?.message || 'Network error resending code.');
    } finally {
      setResending(false);
    }
  };

  const sequentialStages = [
    { num: 1, label: 'Account Type', status: 'completed' as const },
    { num: 2, label: 'Details', status: 'completed' as const },
    {
      num: 3,
      label: 'Email & SMS OTP',
      status:
        emailStageCompleted && phoneStageCompleted
          ? ('completed' as const)
          : stageFailed
          ? ('failed' as const)
          : ('active' as const),
    },
    {
      num: 4,
      label: 'Onboarding',
      status: phoneStageCompleted ? ('active' as const) : ('pending' as const),
    },
    { num: 5, label: 'Activation', status: 'pending' as const },
  ];

  return (
    <div className="space-y-5 animate-fade-in text-slate-800">
      {/* Unified Verification Progress Indicator for Registration */}
      {purpose === 'registration' && (
        <div
          id="sequential-verification-progress"
          className="p-3.5 rounded-2xl bg-slate-900 text-white border border-slate-800 space-y-2.5"
        >
          <div className="flex items-center justify-between text-[11px]">
            <span className="font-extrabold uppercase tracking-wider text-emerald-400">
              Unified Email &amp; SMS OTP Verification
            </span>
            <span className="font-semibold text-slate-300">
              Same 6-Digit Code Sent to Both
            </span>
          </div>
          <div className="grid grid-cols-5 gap-1">
            {sequentialStages.map((st) => (
              <div
                key={st.num}
                className={`flex flex-col items-center p-1.5 rounded-xl border text-center transition-all ${
                  st.status === 'completed'
                    ? 'bg-emerald-950/90 border-emerald-600/70 text-emerald-300'
                    : st.status === 'active'
                    ? 'bg-emerald-600 border-emerald-400 text-white shadow-xs'
                    : st.status === 'failed'
                    ? 'bg-rose-950/90 border-rose-500 text-rose-200'
                    : 'bg-slate-800/70 border-slate-700/70 text-slate-400'
                }`}
              >
                <div className="flex items-center gap-0.5 text-[10px] font-black">
                  {st.status === 'completed' ? <Check className="w-3 h-3 text-emerald-400" /> : <span>{st.num}</span>}
                </div>
                <span className="text-[9px] font-semibold truncate w-full mt-0.5">{st.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="text-center space-y-2">
        <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-600 flex items-center justify-center mx-auto">
          <ShieldCheck className="w-7 h-7" />
        </div>
        <h3 className="text-xl font-extrabold text-slate-900 dark:text-white tracking-tight">
          {purpose === 'login_2fa'
            ? 'New Device Verification'
            : 'Verify Your Account (Email or SMS)'}
        </h3>
        <p className="text-xs text-slate-500 dark:text-slate-400 max-w-sm mx-auto leading-relaxed">
          {purpose === 'login_2fa'
            ? 'We detected a sign-in from a new or unrecognized device. Enter the 6-digit verification code sent to your email address to trust this device for 90 days:'
            : 'We have sent the exact same 6-digit verification code to both your email address and your Nigerian phone number. Enter the code from whichever arrives first:'}
        </p>

        <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
          {purpose === 'registration' ? (
            <>
              <div
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-mono font-bold border ${
                  emailStageCompleted
                    ? 'bg-emerald-50 border-emerald-300 text-emerald-800'
                    : 'bg-slate-900 border-slate-900 text-white'
                }`}
              >
                {emailStageCompleted ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                ) : (
                  <Mail className="w-3.5 h-3.5 text-emerald-400" />
                )}
                <span>{formatMaskedEmail(email)}</span>
              </div>
              <div
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-mono font-bold border ${
                  phoneStageCompleted
                    ? 'bg-emerald-50 border-emerald-300 text-emerald-800'
                    : 'bg-slate-900 border-slate-900 text-white'
                }`}
              >
                {phoneStageCompleted ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                ) : (
                  <Smartphone className="w-3.5 h-3.5 text-emerald-400" />
                )}
                <span>{formatMaskedPhone(phone)}</span>
              </div>
            </>
          ) : (
            <>
              {email && (
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 text-xs font-mono font-bold">
                  <Mail className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                  <span>{formatMaskedEmail(email)}</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="p-3.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs flex items-start gap-2 animate-shake">
          <AlertCircle className="w-4 h-4 shrink-0 text-rose-500 mt-0.5" />
          <div className="space-y-0.5">
            <span className="font-semibold">{error}</span>
            {remainingAttempts !== null && remainingAttempts > 0 && (
              <p className="text-[11px] text-rose-600">
                {remainingAttempts} attempt{remainingAttempts > 1 ? 's' : ''} remaining before this code is invalidated.
              </p>
            )}
          </div>
        </div>
      )}

      {successMessage && (
        <div className="p-3.5 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600" />
          <span className="font-semibold">{successMessage}</span>
        </div>
      )}

      <form onSubmit={handleVerify} className="space-y-6">
        <div>
          <label className="block text-center text-xs font-semibold text-slate-600 mb-3">
            {purpose === 'login_2fa'
              ? 'Enter 6-Digit Verification Code'
              : 'Enter 6-Digit OTP Code (From Email or SMS)'}
          </label>
          <div className="flex items-center justify-center gap-2 sm:gap-3">
            {digits.map((digit, idx) => (
              <input
                key={idx}
                ref={(el) => {
                  inputRefs.current[idx] = el;
                }}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                value={digit}
                onChange={(e) => handleDigitChange(idx, e.target.value)}
                onKeyDown={(e) => handleKeyDown(idx, e)}
                onPaste={idx === 0 ? handlePaste : undefined}
                className="w-11 h-13 sm:w-12 sm:h-14 text-center text-xl font-bold font-mono bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-900 focus:bg-white focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all shadow-xs"
                placeholder="•"
              />
            ))}
          </div>
          <p className="text-[11px] text-slate-400 text-center mt-2 flex items-center justify-center gap-1">
            <Lock className="w-3 h-3" />
            <span>Code expires in 10 minutes • Maximum 5 attempts</span>
          </p>
        </div>

        <button
          id="verify-otp-submit-btn"
          type="submit"
          disabled={loading || digits.join('').length !== 6}
          className="w-full py-3.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold rounded-xl text-xs sm:text-sm transition-all flex items-center justify-center gap-2 shadow-lg shadow-emerald-600/20 cursor-pointer"
        >
          {loading ? (
            <span>Verifying Security Code...</span>
          ) : (
            <>
              <ShieldCheck className="w-4 h-4" />
              <span>
                {purpose === 'login_2fa'
                  ? 'Verify 2FA & Sign In'
                  : 'Verify Code & Activate Account'}
              </span>
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>
      </form>

      <div className="pt-4 border-t border-slate-100 flex flex-col items-center gap-3 text-xs">
        <div className="flex items-center gap-1.5 text-slate-500">
          <span>
            Didn't receive the code yet?
          </span>
          {cooldown > 0 ? (
            <span className="font-semibold text-slate-400">Resend code in {cooldown}s</span>
          ) : (
            <button
              type="button"
              onClick={handleResend}
              disabled={resending}
              className="text-emerald-600 font-bold hover:underline cursor-pointer flex items-center gap-1"
            >
              {resending ? (
                <>
                  <RefreshCw className="w-3 h-3 animate-spin" />
                  <span>Sending...</span>
                </>
              ) : (
                <span>
                  Resend {purpose === 'registration' ? 'Code (Email & SMS)' : 'OTP'}
                </span>
              )}
            </button>
          )}
        </div>

        <div className="text-[11px] text-slate-400 text-center max-w-xs leading-relaxed">
          Having trouble receiving your code? Check your spam folder or contact{' '}
          <a href="mailto:support@jobmatch.ng" className="text-emerald-600 font-semibold hover:underline">
            support@jobmatch.ng
          </a>{' '}
          for account recovery assistance.
        </div>

        <button
          type="button"
          onClick={onBack}
          className="text-slate-500 hover:text-slate-800 text-[11px] font-semibold transition-colors flex items-center gap-1 cursor-pointer"
        >
          <ArrowLeft className="w-3 h-3" />
          <span>{purpose === 'login_2fa' ? 'Back to Sign In' : 'Change Email or Phone Number (Resets Verification)'}</span>
        </button>
      </div>
    </div>
  );
};
